import React, { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, PartyPopper, Users, BarChart3 } from "lucide-react";
import { LazyImageOptimized } from "./ui/lazy-image-optimized";

interface SolutionCard {
  id: string;
  badge: string;
  title: string;
  description: string;
  bgClass: string;
  iconBgClass: string;
  icon: React.ReactNode;
  image: string;
  imageAlt: string;
  textSide: 'left' | 'right';
}

const solutions: SolutionCard[] = [
  {
    id: "recognize",
    badge: "RECOGNITION",
    title: "Recognize & Reward Meaningfully",
    description: "Easy, frequent recognition with built-in points, rewards, and awards — all in one seamless flow your team will love.",
    bgClass: "bg-white",
    iconBgClass: "bg-assembly-orange/10",
    icon: <Star className="h-6 w-6 text-assembly-orange" />,
    image: "/lovable-uploads/27dbdd75-d849-48d4-97e9-71f40d25b831.png",
    imageAlt: "Recognition and rewards interface",
    textSide: 'left'
  },
  {
    id: "celebrate",
    badge: "CELEBRATION",
    title: "Celebrate Every Milestone",
    description: "From birthdays to anniversaries and new employee onboarding, Assembly makes celebrating feel personal, consistent and meaningful.",
    bgClass: "bg-white",
    iconBgClass: "bg-assembly-pink/10",
    icon: <PartyPopper className="h-6 w-6 text-assembly-pink" />,
    image: "/lovable-uploads/3b20dcbc-b876-44e4-b151-5f576b73e3d9.png",
    imageAlt: "Birthday and milestone celebrations",
    textSide: 'left'
  },
  {
    id: "empower",
    badge: "MANAGER ENABLEMENT",
    title: "Empower managers to be more accountable",
    description: "Provide managers with advanced notifications, insights into their teams performance and AI suggested prompts to encourage them to be better managers",
    bgClass: "bg-white",
    iconBgClass: "bg-assembly-purple/10",
    icon: <Users className="h-6 w-6 text-assembly-purple" />,
    image: "/lovable-uploads/7f21cd84-91eb-4485-b5cb-8988e6d35a23.png",
    imageAlt: "Manager enablement dashboard",
    textSide: 'left'
  },
  {
    id: "data",
    badge: "CULTURE INSIGHTS",
    title: "Turn Data into Action",
    description: "Real-time dashboards and Dora AI helps HR spot culture gaps, highlight top contributors, and drive smarter decisions.",
    bgClass: "bg-white",
    iconBgClass: "bg-assembly-blue/10",
    icon: <BarChart3 className="h-6 w-6 text-assembly-blue" />,
    image: "/lovable-uploads/269ea096-5543-42e1-b77d-8f89743ac351.png",
    imageAlt: "Data analytics and insights dashboard",
    textSide: 'left'
  }
];

const SolutionCardComponent = memo(({ solution, index }: { solution: SolutionCard; index: number }) => {
  const linkMap: Record<string, string> = {
    'recognize': '/solutions/recognition',
    'celebrate': '/solutions/culture',
    'empower': '/solutions/managers',
    'data': '/solutions/data-action'
  };
  
  const href = linkMap[solution.id];
  
  return (
    <div
      className="animate-fade-in relative"
      style={{ animationDelay: `${index * 200}ms` }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Content Side - Always on the left */}
        <div className="space-y-4 lg:space-y-6">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${solution.iconBgClass}`}>
              {solution.icon}
            </div>
            <Badge variant="outline" className="bg-white text-xs font-medium uppercase tracking-wide">
              {solution.badge}
            </Badge>
          </div>
          
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
            {solution.title}
          </h3>
          
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            {solution.description}
          </p>

          {href ? (
            <a href={href} className="inline-flex items-center p-0 h-auto font-semibold text-assembly-blue hover:text-assembly-blue/80 transition-colors">
              Learn More →
            </a>
          ) : (
            <Button 
              variant="link" 
              className="p-0 h-auto font-semibold text-assembly-blue hover:text-assembly-blue/80"
            >
              Learn More →
            </Button>
          )}
        </div>
        
        {/* Image Side - Always on the right */}
        <div className="relative w-full max-w-2xl mx-auto lg:mx-0">
          <div className="relative aspect-[4/3] w-full">
            <LazyImageOptimized
              src={solution.image}
              alt={solution.imageAlt}
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

SolutionCardComponent.displayName = 'SolutionCardComponent';

const OptimizedSolutionsGrid = memo(() => {
  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Title Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-fade-in text-center mb-16 relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            What You Can Solve with Assembly
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            From everyday shoutouts to powerful insights — Assembly brings all the tools you need to drive engagement, celebrate wins, and build a culture people love.
          </p>
        </div>

        {/* Solutions Cards */}
        <div className="space-y-16 lg:space-y-20">
          {solutions.map((solution, index) => (
            <SolutionCardComponent
              key={solution.id}
              solution={solution}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
});

OptimizedSolutionsGrid.displayName = 'OptimizedSolutionsGrid';

export default OptimizedSolutionsGrid;